import { computed } from 'vue'

import { useTeamStore } from '@/pinia/useTeamStore'
import { useUserStore } from '@/modules/Auth/useUserStore'
import { defineComposable } from '@/core/utils/defineComposable'

/**
 * A map of all used UTM parameters to add some type-safety and assure no extra parameters are added
 */
const UTM = {
  CAMPAIGN_UPGRADE: ['utm_campaign', '[Darwin] Upgrade your account'],
  CONTENT_BUSINESS_PLAN: ['utm_content', 'business-plan'],
  CONTENT_BYOM: ['utm_content', 'register-a-model'],
  CONTENT_ENTERPRISE_PLAN: ['utm_content', 'enterprise-plan'],
  CONTENT_FILE_LIMIT_REACHED: ['utm_content', 'File Limit Reached'],
  CONTENT_PRO_PLAN: ['utm_content', 'pro-plan'],
  CONTENT_STAGE_CONSENSUS: ['utm_content', 'Consensus Feature'],
  CONTENT_STAGE_LOGIC: ['utm_content', 'Logic Feature'],
  CONTENT_STAGE_MODEL: ['utm_content', 'Model Feature'],
  CONTENT_STAGE_SAMPLING: ['utm_content', 'Sampling Feature'],
  CONTENT_STAGE_WEBHOOK: ['utm_content', 'Webhook Feature'],
  CONTENT_MODEL_LIMIT: ['utm_content', 'Model Training Limit Reached'],
  TERM_ADMIN_ADDON: ['utm_term', 'admin-add-on'],
  TERM_CONTACT_SALES: ['utm_term', 'contact-sales'],
  TERM_ENTERPRISE: ['utm_term', 'enterprise'],
  TERM_TALK_2_SALES_BANNER: ['utm_term', 'talk2sales-banner'],
  TERM_UPGRADE_FREE: ['utm_term', 'upgrade-free'],
  TERM_UPGRADE_PLAN: ['utm_term', 'upgrade plan'],
  TERM_UPGRADE: ['utm_term', 'upgrade'],
  TERM_WORKSPACE_ADDON: ['utm_term', 'workspace-addon'],
  CONTENT_DOWNGRADE: ['utm_term', 'starter-plan-downgrade'],
} as const

/**
 * Builds up a computed object of all "talk to sales" urls used by the platform
 *
 * There are 2 groups of urls. Urls prefixed with OPEN_SIGNUP_ are used in the old open signup flow,
 * while all others are used int he new self-serve + LBB system.
 *
 * https://docs.google.com/spreadsheets/d/1GbmOo4zWRWsm9D5oOhfF6smNx-kyyVI5Kebd6jdGQUI/edit#gid=0
 *
 * Generally, the goal is to gradually replace the OPEN_SIGNUP urls with the regular ones.
 */
export const useUpgradeLocation = defineComposable(() => {
  const userStore = useUserStore()
  const teamStore = useTeamStore()

  const autoFillParams = computed(() =>
    [
      ['firstname', userStore.currentUser?.first_name],
      ['lastname', userStore.currentUser?.last_name],
      ['email', userStore.currentUser?.email],
      ['company', teamStore.currentTeam?.name],
    ]
      .filter((pair): pair is [string, string] => !!pair[1])
      .map(([key, value]) => [key, value]),
  )

  const buildUrl = (params: (typeof UTM)[keyof typeof UTM][]): string => {
    const url = new URL('https://www.v7labs.com/darwin/talk-to-sales')
    url.search = new URLSearchParams(autoFillParams.value).toString()

    url.searchParams.set('utm_medium', 'darwin')
    url.searchParams.set('utm_source', 'website')

    params.forEach(([key, value]) => url.searchParams.set(key, value))
    return url.toString()
  }

  return computed(() => ({
    // current self-serve 2024 + LBB
    REGISTER_MODEL: buildUrl([UTM.CAMPAIGN_UPGRADE, UTM.TERM_UPGRADE_PLAN, UTM.CONTENT_BYOM]),
    PRO: buildUrl([UTM.TERM_CONTACT_SALES, UTM.CONTENT_PRO_PLAN, UTM.CAMPAIGN_UPGRADE]),
    BUSINESS: buildUrl([UTM.TERM_CONTACT_SALES, UTM.CONTENT_BUSINESS_PLAN, UTM.CAMPAIGN_UPGRADE]),
    ENTERPRISE: buildUrl([
      UTM.TERM_CONTACT_SALES,
      UTM.CONTENT_ENTERPRISE_PLAN,
      UTM.CAMPAIGN_UPGRADE,
    ]),
    FILE_LIMIT: buildUrl([UTM.TERM_UPGRADE, UTM.CONTENT_FILE_LIMIT_REACHED]),
    MODEL_LIMIT: buildUrl([UTM.CAMPAIGN_UPGRADE, UTM.TERM_UPGRADE, UTM.CONTENT_MODEL_LIMIT]),
    STAGE_CONSENSUS: buildUrl([
      UTM.CAMPAIGN_UPGRADE,
      UTM.TERM_UPGRADE_PLAN,
      UTM.CONTENT_STAGE_CONSENSUS,
    ]),
    STAGE_LOGIC: buildUrl([UTM.CAMPAIGN_UPGRADE, UTM.TERM_UPGRADE_PLAN, UTM.CONTENT_STAGE_LOGIC]),
    STAGE_MODEL: buildUrl([UTM.CAMPAIGN_UPGRADE, UTM.TERM_UPGRADE_PLAN, UTM.CONTENT_STAGE_MODEL]),
    STAGE_SAMPLING: buildUrl([
      UTM.CAMPAIGN_UPGRADE,
      UTM.TERM_UPGRADE_PLAN,
      UTM.CONTENT_STAGE_SAMPLING,
    ]),
    STAGE_WEBHOOK: buildUrl([
      UTM.CAMPAIGN_UPGRADE,
      UTM.TERM_UPGRADE_PLAN,
      UTM.CONTENT_STAGE_WEBHOOK,
    ]),
    BILLING_BOX: buildUrl([UTM.TERM_UPGRADE_FREE]),
    OPEN_SIGNUP_ENTERPRISE_PLAN: buildUrl([UTM.TERM_ENTERPRISE]),
    OPEN_SIGNUP_TALK_TO_SALES_BANNER: buildUrl([UTM.TERM_TALK_2_SALES_BANNER]),
    OPEN_SIGNUP_ADMIN_ADDON_PURCHASE: buildUrl([UTM.TERM_ADMIN_ADDON]),
    WORKSPACE_ADDON_PURCHASE: buildUrl([UTM.TERM_WORKSPACE_ADDON]),
    DOWNGRADE: buildUrl([UTM.CAMPAIGN_UPGRADE, UTM.TERM_CONTACT_SALES, UTM.CONTENT_DOWNGRADE]),
  }))
})
